// Shared booking-detail row parsers, extracted from BookingViewEditPage.jsx so
// TicketViewPage.jsx can render the same search_payload/actual_payload/fare_info/
// fare_detail detail without duplicating the parsing logic.

export function formatINR(num) {
  if (num === null || num === undefined || Number.isNaN(num)) return "₹0";
  const n = Number(num);
  const hasDecimals = n % 1 !== 0;
  return "₹" + (hasDecimals ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n.toLocaleString("en-IN"));
}

export function toLabel(key) {
  return String(key).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** PascalCase to label: FirstName → First Name, BaseFare → Base Fare */
export function toLabelPascal(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Numeric codes stored in booking payloads, mapped to their names. Values
// mirror navigatio-server/services/air/air.constants.js (TBO codes).
const ENUM_LABELS = {
  journeytype: { 1: "One Way", 2: "Round Trip", 3: "Multi City", 4: "Advance Search", 5: "Special Return" },
  paxtype: { 1: "Adult", 2: "Child", 3: "Infant" },
  passengertype: { 1: "Adult", 2: "Child", 3: "Infant" },
  gender: { 1: "Male", 2: "Female" },
  cabinclass: { 1: "All", 2: "Economy", 3: "Premium Economy", 4: "Business", 5: "Premium Business", 6: "First" },
  flightcabinclass: { 1: "All", 2: "Economy", 3: "Premium Economy", 4: "Business", 5: "Premium Business", 6: "First" },
  tripindicator: { 1: "Onward", 2: "Return" },
  tripindicatorid: { 1: "Onward", 2: "Return" },
  cancellationtype: { 0: "Not Set", 1: "No Show", 2: "Flight Cancelled", 3: "Others" },
  cancelreqtype: { 0: "Not Set", 1: "Full Cancellation", 2: "Partial Cancellation", 3: "Reissuance" },
  requesttype: { 0: "Not Set", 1: "Full Cancellation", 2: "Partial Cancellation", 3: "Reissuance" },
  faretype: { 2: "Regular Fare", 3: "Student Fare", 4: "Armed Force", 5: "Senior Citizen" },
  usertypeid: { 1: "Logged-in User", 9: "Guest" },
};

/** Normalize snake_case / PascalCase keys so pax_type and PaxType match. */
function enumKey(key) {
  return String(key ?? "").replace(/_/g, "").toLowerCase();
}

/**
 * Display text for a payload value: "-" for null/undefined/"", the enum name
 * for known numeric codes (journey_type 1 → One Way), Yes/No for booleans and
 * 0/1 is_* / has_* flags, JSON for objects, otherwise String(val).
 */
export function displayValue(key, val) {
  if (val === null || val === undefined || val === "") return "-";
  const k = enumKey(key);
  const map = ENUM_LABELS[k];
  if (map && map[val] !== undefined) return map[val];
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (/^(is|has)/.test(k) && (val === 0 || val === 1 || val === "0" || val === "1")) return Number(val) === 1 ? "Yes" : "No";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

export const FARE_LIKE_KEYS =new Set(["base_fare", "tax", "per_count", "yq_tax", "additional_txn_fee_ofrd", "additional_txn_fee_pub", "published_fare", "offered_fare", "other_charges", "discount", "service_fee"]);
export const FARE_LIKE_PASCAL = new Set(["BaseFare", "Tax", "YQTax", "Price"]);

/** Expand a fare-like object into readable rows (for admin). */
export function expandFareRows(fareObj, labelPrefix = "  ") {
  if (!fareObj || typeof fareObj !== "object") return [];
  const emptyVal = "-";
  const rows = [];
  Object.entries(fareObj).forEach(([key, val]) => {
    const label = labelPrefix + toLabel(key);
    if (val === null || val === undefined || val === "") {
      rows.push({ label, value: emptyVal });
    } else if (Array.isArray(val)) {
      rows.push({ label, value: `[${val.length} item(s)]` });
    } else if (typeof val === "object" && val !== null) {
      rows.push({ label, value: JSON.stringify(val) });
    } else if (FARE_LIKE_KEYS.has(key) && typeof val === "number") {
      rows.push({ label, value: formatINR(val) });
    } else if (FARE_LIKE_KEYS.has(key) && (typeof val === "string" || typeof val === "number")) {
      const n = Number(val);
      rows.push({ label, value: Number.isNaN(n) ? String(val) : formatINR(n) });
    } else {
      rows.push({ label, value: displayValue(key, val) });
    }
  });
  return rows;
}

/** Expand array of objects (e.g. baggage, meal, seat) into readable rows. */
export function expandArrayOfObjects(arr, sectionLabel, labelPrefix = "  ") {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const emptyVal = "-";
  const rows = [];
  arr.forEach((item, idx) => {
    rows.push({ label: `${labelPrefix}${sectionLabel} ${idx + 1}`, value: emptyVal, isSection: true });
    if (item && typeof item === "object" && !Array.isArray(item)) {
      Object.entries(item).forEach(([key, val]) => {
        const lbl = toLabel(key);
        if (val === null || val === undefined || val === "") {
          rows.push({ label: `${labelPrefix}  ${lbl}`, value: emptyVal });
        } else if (typeof val === "object" && !Array.isArray(val)) {
          rows.push({ label: `${labelPrefix}  ${lbl}`, value: JSON.stringify(val) });
        } else if (Array.isArray(val)) {
          rows.push({ label: `${labelPrefix}  ${lbl}`, value: `[${val.length} item(s)]` });
        } else if (FARE_LIKE_KEYS.has(key) || key === "price") {
          const n = Number(val);
          rows.push({ label: `${labelPrefix}  ${lbl}`, value: Number.isNaN(n) ? String(val) : formatINR(n) });
        } else {
          rows.push({ label: `${labelPrefix}  ${lbl}`, value: displayValue(key, val) });
        }
      });
    } else {
      rows.push({ label: `${labelPrefix}  (raw)`, value: JSON.stringify(item) });
    }
  });
  return rows;
}

/** Normalize fare_info from API (can be object, array, or JSON string) to rows for display. */
export function getFareInfoRows(fareInfo) {
  if (fareInfo === null || fareInfo === undefined) return [];
  let raw = fareInfo;
  while (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  const arr = Array.isArray(raw)
    ? raw
    : raw?.fare_info ?? raw?.fareInfo;
  if (!Array.isArray(arr)) return [];
  return arr.filter((x) => x != null && (x.label != null || x.value != null)).map((x) => ({
    label: String(x.label ?? "-").trim(),
    value: typeof x.value === "number" ? x.value : Number(x.value) || 0,
  }));
}

/** Build label-value rows from search_payload: full payload + fare details + segments mapped. */
export function getSearchPayloadRows(raw) {
  if (raw == null || raw === "") return [];
  let obj = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  if (!obj || typeof obj !== "object") return [];
  const rows = [];
  const skipKeys = new Set(["segments", "passengers", "fare_details"]);

  const emptyVal = "-";
  rows.push({ label: "— Top level —", value: emptyVal, isSection: true });
  Object.entries(obj).forEach(([k, v]) => {
    if (skipKeys.has(k)) return;
    const label = toLabel(k);
    if (v === null || v === undefined || v === "") {
      rows.push({ label, value: emptyVal });
    } else if (Array.isArray(v)) {
      rows.push({ label, value: `[${v.length} item(s)]` });
    } else if (typeof v === "object" && v !== null) {
      const keyLower = k.toLowerCase();
      if (keyLower === "fare" && typeof v === "object") {
        rows.push({ label, value: emptyVal, isSection: true });
        rows.push(...expandFareRows(v, "  "));
      } else {
        rows.push({ label, value: JSON.stringify(v) });
      }
    } else {
      rows.push({ label, value: displayValue(k, v) });
    }
  });

  if (obj.fare_details && typeof obj.fare_details === "object") {
    const fd = obj.fare_details;
    rows.push({ label: "— Fare details —", value: emptyVal, isSection: true });
    if (fd.currency != null) rows.push({ label: "Currency", value: String(fd.currency) });
    if (fd.base_fare != null) rows.push({ label: "Base Fare", value: formatINR(Number(fd.base_fare)), isMoney: true });
    if (fd.tax != null) rows.push({ label: "Taxes & Fees", value: formatINR(Number(fd.tax)), isMoney: true });
    if (fd.published_fare != null) rows.push({ label: "Published Fare", value: formatINR(Number(fd.published_fare)), isMoney: true });
    if (fd.fare_info && Array.isArray(fd.fare_info)) {
      fd.fare_info.forEach((item) => {
        if (item && (item.label != null || item.value != null)) {
          const lbl = String(item.label ?? "Item").trim();
          const val = item.value;
          const num = typeof val === "number" ? val : Number(val);
          rows.push({ label: lbl, value: Number.isNaN(num) ? String(val ?? emptyVal) : formatINR(num), isMoney: true });
        }
      });
    }
    if (fd.total != null) rows.push({ label: "Total", value: formatINR(Number(fd.total)), isMoney: true });
    if (fd.other_charges != null) rows.push({ label: "Other Charges", value: formatINR(Number(fd.other_charges)), isMoney: true });
    if (fd.cancellation_charges != null) rows.push({ label: "Cancellation Charges", value: formatINR(Number(fd.cancellation_charges)), isMoney: true });
    if (fd.break_down && Array.isArray(fd.break_down)) {
      fd.break_down.forEach((b, i) => {
        if (b && typeof b === "object") {
          const sectionLabel = b.passenger_type_s ?? `Break down ${i + 1}`;
          rows.push({ label: sectionLabel, value: emptyVal, isSection: true });
          Object.entries(b).forEach(([k, v]) => {
            const lbl = toLabel(k);
            if (v === null || v === undefined || v === "") {
              rows.push({ label: `  ${lbl}`, value: emptyVal });
            } else if (FARE_LIKE_KEYS.has(k) || /fare|tax|per_count|yq_tax|additional_txn_fee|charges/i.test(k)) {
              const n = Number(v);
              rows.push({ label: `  ${lbl}`, value: Number.isNaN(n) ? String(v) : formatINR(n) });
            } else {
              rows.push({ label: `  ${lbl}`, value: displayValue(k, v) });
            }
          });
        }
      });
    }
  }

  if (obj.segments && Array.isArray(obj.segments)) {
    rows.push({ label: "— Segments —", value: emptyVal, isSection: true });
    rows.push({ label: "Count", value: String(obj.segments.length) });
    obj.segments.forEach((seg, segIdx) => {
      const isNestedArray = Array.isArray(seg);
      const items = isNestedArray ? seg : [seg];
      items.forEach((item, itemIdx) => {
        const segNum = isNestedArray ? `${segIdx + 1}.${itemIdx + 1}` : segIdx + 1;
        rows.push({ label: `Segment ${segNum}`, value: emptyVal, isSection: true });
        if (item && typeof item === "object" && !Array.isArray(item)) {
          Object.entries(item).forEach(([key, val]) => {
            const lbl = toLabel(key);
            if (val !== null && val !== undefined && typeof val === "object" && !Array.isArray(val)) {
              const keyLower = String(key).toLowerCase();
              if (keyLower === "fare" && val !== null && typeof val === "object") {
                rows.push({ label: `  ${lbl}`, value: emptyVal, isSection: true });
                rows.push(...expandFareRows(val, "    "));
              } else {
                rows.push({ label: `  ${lbl}`, value: JSON.stringify(val) });
              }
            } else if (val !== null && val !== undefined && Array.isArray(val)) {
              rows.push({ label: `  ${lbl}`, value: `[${val.length} item(s)]` });
            } else {
              const str = displayValue(key, val);
              rows.push({ label: `  ${lbl}`, value: str });
            }
          });
        } else {
          rows.push({ label: "  (raw)", value: JSON.stringify(item) });
        }
      });
    });
  }

  if (obj.passengers && Array.isArray(obj.passengers)) {
    rows.push({ label: "— Passengers —", value: emptyVal, isSection: true });
    rows.push({ label: "Count", value: String(obj.passengers.length) });
    obj.passengers.forEach((pax, i) => {
      if (pax && typeof pax === "object") {
        rows.push({ label: `Passenger ${i + 1}`, value: emptyVal, isSection: true });
        Object.entries(pax).forEach(([key, val]) => {
          const lbl = toLabel(key);
          if (val !== null && val !== undefined && typeof val === "object" && !Array.isArray(val)) {
            const keyLower = String(key).toLowerCase();
            if (keyLower === "fare" && val !== null && typeof val === "object") {
              rows.push({ label: `  ${lbl}`, value: emptyVal, isSection: true });
              rows.push(...expandFareRows(val, "    "));
            } else {
              rows.push({ label: `  ${lbl}`, value: JSON.stringify(val) });
            }
          } else if (val !== null && val !== undefined && Array.isArray(val)) {
            const keyLower = String(key).toLowerCase();
            if (keyLower === "baggage" || keyLower === "meal" || keyLower === "seat") {
              rows.push({ label: `  ${lbl}`, value: emptyVal, isSection: true });
              rows.push(...expandArrayOfObjects(val, lbl, "    "));
            } else {
              rows.push({ label: `  ${lbl}`, value: `[${val.length} item(s)]` });
            }
          } else {
            const str = displayValue(key, val);
            rows.push({ label: `  ${lbl}`, value: str });
          }
        });
      }
    });
  }

  return rows;
}

/** Rows for actual_payload (PascalCase): TokenId, TraceId, Passengers with Fare/Meal/SeatPreference/Baggage expanded */
export function getActualPayloadRows(raw) {
  if (raw == null || raw === "") return [];
  let obj = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  if (!obj || typeof obj !== "object") return [];
  const emptyVal = "-";
  const rows = [];
  const skipKeys = new Set(["Passengers"]);
  rows.push({ label: "— Top level —", value: emptyVal, isSection: true });
  Object.entries(obj).forEach(([k, v]) => {
    if (skipKeys.has(k)) return;
    const label = toLabelPascal(k);
    if (v === null || v === undefined || v === "") {
      rows.push({ label, value: emptyVal });
    } else if (typeof v === "string" && v.length > 120) {
      rows.push({ label, value: v.slice(0, 120) + "…" });
    } else {
      rows.push({ label, value: displayValue(k, v) });
    }
  });
  if (obj.Passengers && Array.isArray(obj.Passengers)) {
    rows.push({ label: "— Passengers —", value: emptyVal, isSection: true });
    obj.Passengers.forEach((pax, i) => {
      if (!pax || typeof pax !== "object") return;
      rows.push({ label: `Passenger ${i + 1}`, value: emptyVal, isSection: true });
      Object.entries(pax).forEach(([key, val]) => {
        const lbl = toLabelPascal(key);
        if (val === null || val === undefined || val === "") {
          rows.push({ label: `  ${lbl}`, value: emptyVal });
        } else if (key === "Fare" && typeof val === "object" && !Array.isArray(val)) {
          rows.push({ label: `  ${lbl}`, value: emptyVal, isSection: true });
          Object.entries(val).forEach(([k2, v2]) => {
            const l2 = toLabelPascal(k2);
            if (v2 === null || v2 === undefined || v2 === "") rows.push({ label: `    ${l2}`, value: emptyVal });
            else if (FARE_LIKE_PASCAL.has(k2) || k2 === "Price") rows.push({ label: `    ${l2}`, value: formatINR(Number(v2)) });
            else rows.push({ label: `    ${l2}`, value: displayValue(k2, v2) });
          });
        } else if (key === "Meal" && typeof val === "object" && !Array.isArray(val)) {
          rows.push({ label: `  ${lbl}`, value: emptyVal, isSection: true });
          Object.entries(val).forEach(([k2, v2]) => {
            const l2 = toLabelPascal(k2);
            if (v2 === null || v2 === undefined || v2 === "") rows.push({ label: `    ${l2}`, value: emptyVal });
            else if (k2 === "Price") rows.push({ label: `    ${l2}`, value: formatINR(Number(v2)) });
            else rows.push({ label: `    ${l2}`, value: displayValue(k2, v2) });
          });
        } else if (key === "Baggage" && typeof val === "object" && !Array.isArray(val)) {
          rows.push({ label: `  ${lbl}`, value: emptyVal, isSection: true });
          Object.entries(val).forEach(([k2, v2]) => {
            const l2 = toLabelPascal(k2);
            if (v2 === null || v2 === undefined || v2 === "") rows.push({ label: `    ${l2}`, value: emptyVal });
            else if (k2 === "Price") rows.push({ label: `    ${l2}`, value: formatINR(Number(v2)) });
            else rows.push({ label: `    ${l2}`, value: displayValue(k2, v2) });
          });
        } else if (key === "SeatPreference" && Array.isArray(val)) {
          rows.push({ label: `  ${lbl}`, value: emptyVal, isSection: true });
          val.forEach((item, idx) => {
            rows.push({ label: `    Seat ${idx + 1}`, value: emptyVal, isSection: true });
            if (item && typeof item === "object") {
              Object.entries(item).forEach(([k2, v2]) => {
                const l2 = toLabelPascal(k2);
                rows.push({ label: `      ${l2}`, value: displayValue(k2, v2) });
              });
            }
          });
        } else if (typeof val === "object") {
          rows.push({ label: `  ${lbl}`, value: JSON.stringify(val) });
        } else {
          rows.push({ label: `  ${lbl}`, value: displayValue(key, val) });
        }
      });
    });
  }
  return rows;
}

/** Rows for fare_detail: trace_id, index_id, flags, fare_details (break_down, fare_info, total), segments, fare_rules, mini_fare_rules */
export function getFareDetailRows(raw) {
  if (raw == null || raw === "") return [];
  let obj = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  if (!obj || typeof obj !== "object") return [];
  const emptyVal = "-";
  const rows = [];
  const skipKeys = new Set(["segments", "fare_details", "fare_rules", "mini_fare_rules"]);
  rows.push({ label: "— Top level —", value: emptyVal, isSection: true });
  Object.entries(obj).forEach(([k, v]) => {
    if (skipKeys.has(k)) return;
    const label = toLabel(k);
    if (v === null || v === undefined || v === "") rows.push({ label, value: emptyVal });
    else if (typeof v === "string" && v.length > 100) rows.push({ label, value: v.slice(0, 100) + "…" });
    else if (Array.isArray(v)) rows.push({ label, value: `[${v.length} item(s)]` });
    else if (typeof v === "object") rows.push({ label, value: JSON.stringify(v) });
    else rows.push({ label, value: displayValue(k, v) });
  });
  if (obj.fare_details && typeof obj.fare_details === "object") {
    const fd = obj.fare_details;
    rows.push({ label: "— Fare details —", value: emptyVal, isSection: true });
    ["currency", "base_fare", "tax", "yq_tax", "published_fare", "offered_fare", "other_charges", "cancellation_charges", "total"].forEach((k) => {
      if (fd[k] == null) return;
      const label = toLabel(k);
      if (FARE_LIKE_KEYS.has(k) || /fare|tax|charge/i.test(k)) rows.push({ label, value: formatINR(Number(fd[k])) });
      else rows.push({ label, value: String(fd[k]) });
    });
    if (fd.fare_info && Array.isArray(fd.fare_info)) {
      fd.fare_info.forEach((item) => {
        if (item && (item.label != null || item.value != null))
          rows.push({ label: String(item.label ?? "").trim(), value: formatINR(Number(item.value)) });
      });
    }
    if (fd.break_down && Array.isArray(fd.break_down)) {
      fd.break_down.forEach((b, i) => {
        if (!b || typeof b !== "object") return;
        const pt = b.passenger_type_s ?? `Passenger ${i + 1}`;
        rows.push({ label: pt, value: emptyVal, isSection: true });
        Object.entries(b).forEach(([k2, v2]) => {
          const l2 = toLabel(k2);
          if (v2 === null || v2 === undefined || v2 === "") rows.push({ label: `  ${l2}`, value: emptyVal });
          else if (FARE_LIKE_KEYS.has(k2) || /fare|tax|per_count|yq|additional/i.test(k2)) rows.push({ label: `  ${l2}`, value: formatINR(Number(v2)) });
          else rows.push({ label: `  ${l2}`, value: displayValue(k2, v2) });
        });
      });
    }
  }
  if (obj.segments && Array.isArray(obj.segments)) {
    rows.push({ label: "— Segments —", value: emptyVal, isSection: true });
    obj.segments.forEach((segList, listIdx) => {
      const items = Array.isArray(segList) ? segList : [segList];
      items.forEach((seg, segIdx) => {
        if (!seg || typeof seg !== "object") return;
        const segNum = items.length > 1 ? `${listIdx + 1}.${segIdx + 1}` : listIdx + 1;
        rows.push({ label: `Segment ${segNum}`, value: emptyVal, isSection: true });
        ["duration", "baggage", "cabin_baggage", "cabin_class", "flight_status"].forEach((k) => {
          if (seg[k] != null) rows.push({ label: `  ${toLabel(k)}`, value: displayValue(k, seg[k]) });
        });
        if (seg.airline && typeof seg.airline === "object") {
          rows.push({ label: "  Airline", value: emptyVal, isSection: true });
          Object.entries(seg.airline).forEach(([k2, v2]) => {
            if (v2 == null || v2 === "") return;
            rows.push({ label: `    ${toLabel(k2)}`, value: displayValue(k2, v2) });
          });
        }
        if (seg.origin && typeof seg.origin === "object") {
          const o = seg.origin;
          rows.push({ label: `  Origin`, value: [o.airport_code, o.city_name, o.dep_time].filter(Boolean).join(" • ") || emptyVal });
        }
        if (seg.destination && typeof seg.destination === "object") {
          const d = seg.destination;
          rows.push({ label: `  Destination`, value: [d.airport_code, d.city_name, d.arr_time].filter(Boolean).join(" • ") || emptyVal });
        }
      });
    });
  }
  if (obj.fare_rules && typeof obj.fare_rules === "object" && obj.fare_rules.details && Array.isArray(obj.fare_rules.details)) {
    rows.push({ label: "— Fare rules —", value: emptyVal, isSection: true });
    obj.fare_rules.details.forEach((rule, i) => {
      if (rule && (rule.content != null || rule.destination != null)) {
        rows.push({ label: `Rule ${i + 1}${rule.destination ? ` (${rule.destination})` : ""}`, value: emptyVal, isSection: true });
        if (rule.destination != null) rows.push({ label: "  Destination", value: String(rule.destination) });
        if (rule.content != null) rows.push({ label: "  Content", value: String(rule.content).slice(0, 500) + (String(rule.content).length > 500 ? "…" : "") });
      }
    });
  }
  if (obj.mini_fare_rules && typeof obj.mini_fare_rules === "object") {
    rows.push({ label: "— Mini fare rules —", value: emptyVal, isSection: true });
    Object.entries(obj.mini_fare_rules).forEach(([route, rules]) => {
      if (!rules || typeof rules !== "object") return;
      rows.push({ label: route, value: emptyVal, isSection: true });
      ["Cancellation", "Reissue", "Other"].forEach((cat) => {
        const arr = rules[cat];
        if (!Array.isArray(arr) || arr.length === 0) return;
        arr.forEach((item, idx) => {
          if (item && typeof item === "object") {
            const w = item.window != null ? item.window : "";
            const p = item.penalty != null ? item.penalty : "";
            rows.push({ label: `  ${cat} ${idx + 1}`, value: [w, p].filter(Boolean).join(" — ") || emptyVal });
          }
        });
      });
    });
  }
  return rows;
}
